<!--
    AssetImageUpload

    ** DESCRIPTION **
    General purpose component for uploading media to an asset object

    ** PROPS **
    - asset_type {str} => according to destination asset.type e.g. "vehicle". This is used to customise the UI and various settings
    - media_types {arr} => the types of media permitted to upload
-->

<template>
    <div class="mb-6">
        <FormFileUploadDropZone @files-dropped="addFiles">
            <svg class="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true">
                <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            <div class="flex text-sm text-gray-600">
                <label :for="field_id" class="relative cursor-pointer rounded-md font-medium text-blue-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2 hover:text-blue-500">
                    <span>{{ upload_label }}</span>
                    <input :id="field_id" :name="field_id" type="file" class="sr-only" multiple v-on:change="onInputChange" accept="image/png, image/jpeg">
                </label>
                <p class="pl-1">or drag and drop</p>
            </div>
            <p class="text-xs text-gray-500">PNG, JPG up to 10MB</p>
        </FormFileUploadDropZone>
    </div>

    <FormFileUploadQueue :files="files" class="mb-6" :removeFile="removeFile" media_type="image" :hide_complete="true" />

    <!--<Button v-on:click="uploadFiles(files)" :disabled="uploadable_files.length === 0">Upload</Button>-->
</template>

<script>
    import useFileList from '@lib/file-list'
    import * as AssetService from '@ser/asset'
    import { useAssetStore } from '@sto/asset.store'
    import { useSystemStore } from '@sto/system.store'
    import { computed, watch } from 'vue';
    import { useRoute } from 'vue-router';
    export default {
        props: ['asset_id', 'asset_type', 'media_types', 'field_id', 'base_file_name', 'hide_complete'],
        setup(props, {emit}) {

            const ASSET_STORE = useAssetStore()
            const SYSTEM_STORE = useSystemStore()
            const ROUTE = useRoute()

            const { files, addFiles, removeFile } = useFileList()

            const uploadable_files = computed(() => {
                if (!Array.isArray(files.value)) return []
                return files.value.filter(f => f.status == 'ready') || []
            })

            const onInputChange = e => {
                addFiles(e.target.files)
                e.target.value = null
            }

            const timeout = ms => {
                return new Promise(resolve => setTimeout(resolve, ms));
            }

            const uploadFile = async (file, index) => {
                // make sure we're not trying to re-upload existing images 
                if (file.status == 'complete') return

                await timeout(1000 * index)


                file.status = 'loading'
                const payload = {
                    file: file.file,
                    file_name: props.base_file_name || 'asset',
                    type: 'image',
                    sort_order: 999
                }
                try {
                    const upload = await AssetService.addMedia(props.asset_id, payload)
                    file.status = 'complete'
                } catch (error) {
                    console.error('AssetService.addMedia error', error)
                    file.status = 'error'
                }
            }

            const upload_label = computed(() => {
                if (!Array.isArray(props.media_types) || props.media_types.length > 1) return 'Upload a file'

                switch(props.media_types[0]) {
                    case 'image':
                        return 'Upload a photo'
                    default:
                        return 'Upload a file'
                }
            })

            watch(
                () => files.value,
                (new_value, old_value) => {
                    setTimeout(() => {
                        uploadFiles()
                    }, 1000)
                }
            )

            const uploadFiles = async () => {
                // const uploads = await Promise.all(files.value.map((file, index) => uploadFile(file, index)))
                // Upload files sequentially, one at a time
                for (const file of files.value) {
                    await uploadFile(file, 0)
                }
                await ASSET_STORE.getMedia(props.asset_id, props.media_types)
                await ASSET_STORE.updateConnected(props.asset_id)
                outcomeReport()
            }

            const outcomeReport = () => {
                const failures = files.value.filter(f => f.status == 'error')

                if (Array.isArray(failures) && failures.length > 0 ) {
                    SYSTEM_STORE.addError('Some photos failed to upload')
                } else {
                    SYSTEM_STORE.addSuccess('Your photos have been uploaded')
                }

                emit('upload_complete')
            }

            return {
                onInputChange,
                addFiles,
                uploadFiles,
                removeFile,
                files,
                uploadable_files,
                upload_label
            }
        },
    }
</script>